import Drawer from "@via/components/drawer";
import DeleteModal from "@via/components/delete-modal/delete-modal.tsx";
import { SkeletonLoader } from "@via/components/loading/loading";
import AdminRunEnvironmentForm from "@via/pages/account/admin/organizations/panels/admin-run-environment-form.tsx";
import AwsSubmitterForm from "@via/pages/account/admin/organizations/panels/aws-submitter-form.tsx";
import {
  useDeleteAdminOrganizationRunEnvironment,
  useGetAdminOrganizationRunEnvironments,
} from "@via/pages/account/admin/organizations/organizations.api.tsx";
import { EmptyDataIcon } from "@via/utils/custom-icons.tsx";
import {
  AdminOrganizationRunEnvironment,
  GetUserOrganizationDetailsForAdminResponseBodyItem,
} from "common";
import { useRef, useState, type JSX } from "react";

/**
 * The Run Environments tab: what an organization can launch runs on, managed without joining it.
 *
 * A live list tab like Members, so there is no modal level Save; each action commits on its own.
 *
 * ⚠️ The drawer needs `z-[110]`. The modal that contains this panel is `z-[100]` and the drawer
 * defaults to `z-[99]`, and the two dialogs portal to the SAME root as siblings rather than
 * nesting, so paint order comes down to those numbers. Measured at planning; without the override
 * the form opens behind the modal and looks like nothing happened.
 */
export default function RunEnvironmentsPanel({
  organization,
}: {
  organization: GetUserOrganizationDetailsForAdminResponseBodyItem;
}): JSX.Element {
  const organizationId = organization.id;
  const { data, isLoading, isError } = useGetAdminOrganizationRunEnvironments(
    organizationId,
    !!organizationId
  );

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>(undefined);
  const [submitterTarget, setSubmitterTarget] =
    useState<AdminOrganizationRunEnvironment | null>(null);
  const [toDelete, setToDelete] =
    useState<AdminOrganizationRunEnvironment | null>(null);

  // Focus goes back where it came from when the drawer closes, so a keyboard user is not dropped
  // at the top of the modal.
  const openerRef = useRef<HTMLElement | null>(null);

  const { mutateAsync: remove, isPending: isDeleting } =
    useDeleteAdminOrganizationRunEnvironment();

  const runEnvironments = data ?? [];

  const openDrawer = (
    event: React.MouseEvent<HTMLButtonElement>,
    runEnvironmentId?: number
  ): void => {
    openerRef.current = event.currentTarget;
    setEditingId(runEnvironmentId);
    setDrawerOpen(true);
  };

  const closeDrawer = (open: boolean): void => {
    setDrawerOpen(open);
    if (!open) openerRef.current?.focus();
  };

  return (
    <div data-testid="run-environments-panel" className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="text-sm text-gray-700">
          What this organization can launch runs on. Environments created here
          are owned by the organization&apos;s own administrator, and only
          credentials belonging to this organization can be attached to them.
        </div>
        <button
          type="button"
          onClick={event => openDrawer(event)}
          className="shrink-0 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
          Create
        </button>
      </div>

      <div className="rounded-md border border-gray-200">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-900">
          Run environments ({runEnvironments.length})
        </div>
        {isLoading ? (
          <div className="p-4">
            <SkeletonLoader count={1} />
          </div>
        ) : isError ? (
          <div className="p-6 text-sm text-red-600">
            This organization&apos;s run environments could not be loaded.
          </div>
        ) : runEnvironments.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-6 text-sm text-gray-600">
            <EmptyDataIcon />
            <div>This organization has no run environments yet.</div>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {runEnvironments.map(runEnvironment => (
              <li
                key={runEnvironment.id}
                data-testid={`run-environment-row-${runEnvironment.id}`}
                className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900">
                    {runEnvironment.name}{" "}
                    <span className="font-normal text-gray-500">
                      #{runEnvironment.id}
                    </span>
                  </div>
                  <div className="mt-1 text-gray-600">
                    {runEnvironment.executor ?? "no executor"}
                    {runEnvironment.executorJob
                      ? ` · ${runEnvironment.executorJob}`
                      : ""}
                  </div>
                  <div className="mt-1 text-gray-500">
                    Owned by {runEnvironment.ownerEmail ?? "an unknown account"}
                  </div>
                  <div className="mt-1 flex gap-2 text-xs">
                    {runEnvironment.isSystemGenerated && (
                      <span className="rounded bg-gray-100 px-2 py-0.5 text-gray-700">
                        Platform managed
                      </span>
                    )}
                    {runEnvironment.boundAttachmentId && (
                      <span className="rounded bg-indigo-50 px-2 py-0.5 text-indigo-700">
                        Bound to a boundary
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex shrink-0 gap-2">
                  {runEnvironment.isSystemGenerated ? (
                    // Read only apart from the submitter identity, and the server agrees: any
                    // other field on this row is refused for the whole request.
                    <button
                      type="button"
                      onClick={() => setSubmitterTarget(runEnvironment)}
                      className="rounded-md bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                      AWS submitter
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={event => openDrawer(event, runEnvironment.id)}
                        className="rounded-md bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setToDelete(runEnvironment)}
                        className="rounded-md bg-white px-2 py-1 text-sm font-semibold text-red-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                        Remove
                      </button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {submitterTarget && (
        <AwsSubmitterForm
          organizationId={organizationId}
          runEnvironment={submitterTarget}
          onDone={() => setSubmitterTarget(null)}
        />
      )}

      <Drawer
        id="admin-run-environment"
        open={isDrawerOpen}
        setOpen={closeDrawer}
        className="z-[110]">
        <AdminRunEnvironmentForm
          key={editingId ?? "new"}
          organizationId={organizationId}
          runEnvironmentId={editingId}
          setOpen={closeDrawer}
        />
      </Drawer>

      <DeleteModal
        item={toDelete}
        setItem={setToDelete}
        deleteFunction={async () => {
          await remove({ organizationId, runEnvironment: toDelete! });
        }}
        isLoading={isDeleting}
        title="Delete Run Environment"
      />
    </div>
  );
}
